import { SITE } from "@/config/site";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Reveal } from "@/components/Reveal";
import { BookButton } from "@/components/GoldButton";
import { AskChittiButton } from "@/components/AskChittiButton";
import { StarPattern } from "@/components/Ornament";
import { ArrowUpRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import "@/styles/theme-v2.css";

// ============================================================================
// CHIEF GUEST — Usha Mangeshkar.
// The only new, verified fact behind this section: she is the Chief Guest
// and is expected to perform two songs at Chitti Ayee Hai (SITE.chiefGuest,
// config/site.js). Deliberately no biography, awards, dates, relationships,
// quotes, career stats, song names or performance-history claims — none of
// that has been confirmed for this event, so none of it appears here.
//
// No approved photograph of her exists in the project's assets. Rather than
// an empty photo slot (which read as an unfinished/broken page), the no-photo
// state below is its own deliberate visual: a gold ornamental "seal" medallion
// — a monogram in a spotlighted circular frame — styled as a royal-invitation
// motif, not a placeholder. It shares no shape with the arch-topped portrait
// frames used everywhere else on the site (Artist, legends, Venue), so it
// never reads as "photo missing." Once a real, approved portrait is supplied,
// flip SITE.chiefGuest.hasPhoto/photo — same swap pattern used everywhere
// else on the site — and this component switches to the standard arch-frame
// portrait treatment automatically.
//
// Placed as its own chapter (04) between the Artist section and Event
// Details, so it reads as a distinct "special presence" moment in the
// evening's story rather than another performer card.
// ============================================================================

export function ChiefGuest() {
  const guest = SITE.chiefGuest;
  const initials = guest.name
    .split(" ")
    .map((w) => w[0])
    .join("");

  return (
    <section
      id="chief-guest"
      data-testid="chief-guest-section"
      className="relative overflow-hidden px-5 py-24 sm:px-8 sm:py-32 lg:py-40"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#D4AF37]/[0.08] blur-[130px]" />
        <div className="absolute bottom-0 right-0 h-[320px] w-[420px] rounded-full bg-[#7A1B2B]/[0.07] blur-[110px]" />
        <StarPattern id="chief-guest-bg-pattern" opacity={0.03} />
      </div>

      <div className="relative mx-auto max-w-3xl text-center">
        <ChapterHeading number="04" title="Chief Guest" center testId="chief-guest-heading" />

        {guest.hasPhoto && guest.photo ? (
          <Reveal delay={0.1}>
            <div className="relative mx-auto mt-12 w-full max-w-[300px]">
              <div className="cah-light-sweep arch-frame relative aspect-[3/4.2] w-full overflow-hidden border border-[#D4AF37]/40 bg-[#0E0C0A]">
                <img
                  src={guest.photo}
                  alt={`${guest.name} — Chief Guest`}
                  data-testid="chief-guest-photo"
                  className="cah-kenburns absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="pointer-events-none absolute inset-[8px] border border-[#F3E5AB]/15" aria-hidden="true" />
              </div>
              <div
                className="arch-frame absolute -right-4 top-4 -z-10 h-full w-full border border-[#D4AF37]/15"
                aria-hidden="true"
              />
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            {/* Ornamental seal / medallion — a royal-invitation motif standing
                in for a portrait, styled deliberately rather than left empty. */}
            <div className="relative mx-auto mt-10 flex h-48 w-48 items-center justify-center rounded-full border-2 border-dashed border-[#D4AF37]/35 sm:h-56 sm:w-56">
              <div className="cah-light-sweep relative flex h-[84%] w-[84%] items-center justify-center overflow-hidden rounded-full border border-[#D4AF37]/50 bg-gradient-to-b from-[#171310] to-[#0A0805] shadow-[0_0_60px_-12px_rgba(212,175,55,0.28)]">
                <StarPattern id="chief-guest-medallion-pattern" opacity={0.22} className="rounded-full" />
                <div
                  className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_32%,rgba(212,175,55,0.2),transparent_70%)]"
                  aria-hidden="true"
                />
                <div className="pointer-events-none absolute inset-[6px] rounded-full border border-[#F3E5AB]/15" aria-hidden="true" />
                <div className="relative flex flex-col items-center gap-2 px-4 text-center">
                  <span
                    className="h-px w-8 bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent"
                    aria-hidden="true"
                  />
                  <span
                    data-testid="chief-guest-monogram"
                    className="font-display gold-text text-4xl tracking-[0.06em] sm:text-5xl"
                  >
                    {initials}
                  </span>
                  <span
                    className="h-px w-8 bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent"
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.18}>
          <h3
            data-testid="chief-guest-name"
            className="font-display gold-text mt-10 text-3xl uppercase tracking-tight sm:text-4xl lg:text-5xl"
          >
            {guest.name}
          </h3>
          <div
            className="cah-hairline-grow mx-auto mt-4 h-px w-16"
            style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }}
            aria-hidden="true"
          />
        </Reveal>

        <Reveal delay={0.24}>
          <p className="font-accent mx-auto mt-5 max-w-xl text-lg italic leading-relaxed text-[#C8C2B7] sm:text-xl">
            {guest.presenceLine}
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] px-5 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#F3E5AB] sm:text-sm">
              {guest.role}
            </span>
            {guest.performanceNote && (
              <span className="rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] px-5 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#D4AF37]/90 sm:text-sm">
                {guest.performanceNote}
              </span>
            )}
          </div>
        </Reveal>

        {guest.profileUrl && (
          <Reveal delay={0.34}>
            <a
              href={guest.profileUrl}
              data-testid="chief-guest-profile-link"
              onClick={() => trackEvent("performer_profile_click", { performer: guest.name, cta_location: "chief_guest" })}
              className="mt-8 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#D4AF37] underline decoration-[#D4AF37]/30 underline-offset-4 transition-colors hover:text-[#E6CA65]"
            >
              Discover Her Story
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </Reveal>
        )}

        <Reveal delay={0.38}>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <BookButton location="chief_guest" label="Book Your Tickets" testId="chief-guest-book-tickets-btn" />
            <AskChittiButton location="chief_guest" label="Ask Chitti AI" testId="chief-guest-ask-chitti-btn" />
          </div>
          <p className="mt-5 text-sm text-[#918A7E]">
            Don&rsquo;t miss this evening — from ₹{SITE.priceFrom} on BookMyShow · {SITE.listingStatus}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
